import * as React from "react"
import {
  ArrowRight,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  HelpCircle,
  Home,
  MapPin,
  User,
} from "react-feather"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface BaseCardProps {
  className?: string
  children?: React.ReactNode
}

const Card = React.forwardRef<HTMLDivElement, BaseCardProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-md bg-white border border-slate-300 shadow-sm overflow-hidden",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
)
Card.displayName = "Card"

const CardContent = React.forwardRef<HTMLDivElement, BaseCardProps>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6", className)} {...props} />
)
CardContent.displayName = "CardContent"

const CardHeader = React.forwardRef<HTMLDivElement, BaseCardProps>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6", className)} {...props} />
)
CardHeader.displayName = "CardHeader"

interface DefaultCardProps extends BaseCardProps {
  title: string
  text: string
  subText?: string
  count?: number
  status?: string
  statusVariant?: "default" | "success" | "secondary" | "destructive" | "warning" | "info"
  icon?: React.ReactNode
}

const DefaultCard = React.forwardRef<HTMLDivElement, DefaultCardProps>(
  ({ title, text, subText, count, status, statusVariant, icon, className, ...props }, ref) => {
    const getBadgeProps = () => {
      if (statusVariant) {
        return { variant: statusVariant }
      }
      // Auto-detect variant based on status text
      if (status === "進行中" || status === "対応中") {
        return {
          variant: "success" as const,
          className: "bg-[#25D2AF] text-white",
        }
      }
      if (status === "完了" || status === "解決済み") {
        return {
          variant: "secondary" as const,
          className: "bg-slate-200 text-slate-600",
        }
      }
      if (status === "要対応" || status === "緊急") {
        return { variant: "destructive" as const }
      }
      return { variant: "default" as const }
    }

    return (
      <Card ref={ref} className={cn("p-4 space-y-2", className)} {...props}>
        {(subText || count !== undefined) && (
          <div className="flex justify-between items-center">
            {subText && (
              <span className="text-xs font-semibold text-slate-400 tracking-tight">{subText}</span>
            )}
            {count !== undefined && (
              <div className="bg-indigo-100 text-slate-700 text-xs font-bold px-2 py-1 rounded-full min-h-[22px] flex items-center justify-center">
                {count}
              </div>
            )}
          </div>
        )}
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            {icon}
            <h3 className="text-base font-semibold text-slate-700 tracking-tight leading-5">
              {title}
            </h3>
          </div>
          <p className="text-sm font-medium text-slate-500 leading-5 tracking-tight">{text}</p>
        </div>
        {status && <Badge {...getBadgeProps()}>{status}</Badge>}
      </Card>
    )
  }
)
DefaultCard.displayName = "DefaultCard"

interface SummaryCardItem {
  label: React.ReactNode
  value: React.ReactNode
  colSpan?: 1 | 2 | 3 | 4
  labelClassName?: string
  valueClassName?: string
}

interface SummaryCardProps extends BaseCardProps {
  title: string
  icon?: React.ReactNode
  action?: React.ReactNode
  items: SummaryCardItem[]
  columns?: 1 | 2 | 3 | 4
  contentClassName?: string
}

const columnClassMap: Record<NonNullable<SummaryCardProps["columns"]>, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
}

const SummaryCard = React.forwardRef<HTMLDivElement, SummaryCardProps>(
  ({ title, icon, action, items, columns = 2, contentClassName, className, ...props }, ref) => (
    <Card ref={ref} className={className} {...props}>
      <CardContent className={cn("space-y-4 p-4", contentClassName)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
          </div>
          {action}
        </div>

        <div className={cn("grid gap-4", columnClassMap[columns])}>
          {items.map((item, index) => {
            const key =
              typeof item.label === "string"
                ? `summary-item-${item.label}`
                : `summary-item-${index}`

            return (
              <div
                key={key}
                className={cn("space-y-1", {
                  "col-span-1": item.colSpan === 1,
                  "col-span-2": item.colSpan === 2,
                  "col-span-3": item.colSpan === 3,
                  "col-span-4": item.colSpan === 4,
                })}
              >
                <p className={cn("text-xs font-semibold text-slate-400", item.labelClassName)}>
                  {item.label}
                </p>
                <div className={cn("text-sm text-slate-600", item.valueClassName)}>
                  {item.value}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
)
SummaryCard.displayName = "SummaryCard"

interface ChatListCardProps extends BaseCardProps {
  title: string
  titleIcon?: React.ReactElement
  description: string
  status?: string
  date: string
  count?: number
  author?: string
}

const ChatListCard = React.forwardRef<HTMLDivElement, ChatListCardProps>(
  ({ title, titleIcon, description, status, date, count, author, className, ...props }, ref) => (
    <Card ref={ref} className={cn("p-4 cursor-pointer hover:bg-gray-50", className)} {...props}>
      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 tracking-tight leading-[14px]">
              {date}
            </span>
          </div>

          {author && (
            <p className="flex items-center text-xs font-semibold gap-1">
              <User className="size-4" />
              作成者: {author}
            </p>
          )}
          <div className="flex items-center gap-2.5">
            <h3
              className={cn(
                "text-base font-semibold text-slate-700 tracking-tight leading-5",
                titleIcon && "flex items-center gap-1"
              )}
            >
              {titleIcon && titleIcon}
              {title}
            </h3>
            {count && (
              <div className="bg-indigo-100 text-slate-700 text-xs font-bold px-2 py-1 rounded-full min-h-[22px] flex items-center justify-center">
                {count}
              </div>
            )}
          </div>

          <p className="text-sm font-medium text-slate-500 leading-5 tracking-tight">
            {description}
          </p>
          {status && <Badge>{status}</Badge>}
        </div>
        <ArrowRight className="w-5 h-5 text-slate-700 flex-shrink-0" />
      </div>
    </Card>
  )
)
ChatListCard.displayName = "ChatListCard"

interface EmptyCardProps extends BaseCardProps {
  text: string
}

const EmptyCard = React.forwardRef<HTMLDivElement, EmptyCardProps>(
  ({ text, className, ...props }, ref) => (
    <Card ref={ref} className={cn("bg-slate-100 p-4 border-none", className)} {...props}>
      <p className="text-sm font-semibold text-slate-400 text-center tracking-tight leading-[18px]">
        {text}
      </p>
    </Card>
  )
)
EmptyCard.displayName = "EmptyCard"

interface DateCardProps extends BaseCardProps {
  title: string
  count: string
  countText: string
  completed?: boolean
  icon?: React.ComponentType<{ className?: string }>
  bgColor?: string
  borderColor?: string
  textColor?: string
}

const DateCard = React.forwardRef<HTMLDivElement, DateCardProps>(
  (
    {
      title,
      count,
      countText,
      completed = false,
      icon: Icon,
      bgColor = "bg-teal-500/[0.03]",
      borderColor = "border-teal-400",
      textColor = "text-teal-500",
      className,
      ...props
    },
    ref
  ) => (
    <Card
      ref={ref}
      className={cn(`p-4 border ${borderColor} ${bgColor} relative`, className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-600 tracking-tight leading-[18px]">
          {title}
        </h3>
      </div>

      <div className="flex items-center text-center mt-2 gap-2">
        <p className={`text-3xl font-bold ${textColor} leading-3`}>{count}</p>
        <p className="text-slate-400">{countText}</p>
      </div>

      {completed && (
        <CheckCircle
          className={`absolute top-4 right-4 w-5.5 h-5.5 ${textColor} bg-white rounded-full mt-0`}
        />
      )}
      {Icon && !completed && <Icon className={`absolute top-4 right-4 h-5 w-5 ${textColor}`} />}
    </Card>
  )
)
DateCard.displayName = "DateCard"

interface PropertyListCardProps extends BaseCardProps {
  title: string
  address: string
  stats: Array<{ label: string; value: string }>
  buttonText?: string
  onEdit?: () => void
  onButtonClick?: () => void
}

const PropertyListCard = React.forwardRef<HTMLDivElement, PropertyListCardProps>(
  ({ title, address, stats, buttonText, onEdit, onButtonClick, className, ...props }, ref) => (
    <Card ref={ref} className={cn("p-4 space-y-4", className)} {...props}>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-600 tracking-tight leading-5 line-clamp-2 pr-6">
          {title}
        </h3>
        {onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="w-3.5 h-3.5 mr-1" />
            編集
          </Button>
        )}
      </div>

      <div className="flex items-center gap-1">
        <MapPin className="min-w-3.5 w-3.5 h-3.5 text-slate-500" />
        <p className="text-sm font-medium text-slate-500 tracking-tight leading-5 line-clamp-1">
          {address}
        </p>
      </div>

      <div className="flex items-center justify-center gap-5 py-2.5 h-[65px]">
        {stats.map((stat, index) => (
          <React.Fragment key={`stat-${stat.label}`}>
            <div className="flex flex-col gap-2 flex-1">
              <p className="text-sm font-semibold text-slate-600 text-center tracking-tight leading-[18px]">
                {stat.label}
              </p>
              <p className="text-2xl font-bold text-slate-600 text-center leading-5">
                {stat.value}
              </p>
            </div>
            {index < stats.length - 1 && <div className="w-0 h-12 border-l border-slate-300" />}
          </React.Fragment>
        ))}
      </div>

      {buttonText && (
        <Button variant="outline" onClick={onButtonClick} className="w-full">
          {buttonText}
        </Button>
      )}
    </Card>
  )
)
PropertyListCard.displayName = "PropertyListCard"

interface MemoListCardProps extends BaseCardProps {
  title: string
  description: string
  date: string
  buttonText?: string
  imageUrls?: string[]
  imageAlt?: string
  onEdit?: () => void
  onButtonClick?: () => void
}

const MemoListCard = React.forwardRef<HTMLDivElement, MemoListCardProps>(
  (
    {
      title,
      description,
      date,
      buttonText,
      imageUrls,
      imageAlt,
      onEdit,
      onButtonClick,
      className,
      ...props
    },
    ref
  ) => {
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0)

    const nextImage = () => {
      if (imageUrls && imageUrls.length > 1) {
        setCurrentImageIndex((prev) => (prev + 1) % imageUrls.length)
      }
    }

    const prevImage = () => {
      if (imageUrls && imageUrls.length > 1) {
        setCurrentImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length)
      }
    }

    return (
      <Card ref={ref} className={cn("p-4 space-y-4", className)} {...props}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 tracking-tight">{date}</span>
            {onEdit && (
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Edit className="w-3.5 h-3.5 mr-1" />
                編集
              </Button>
            )}
          </div>

          <h3 className="text-base font-semibold text-slate-600 tracking-tight leading-5">
            {title}
          </h3>

          <p className="text-sm font-medium text-slate-500 leading-5 tracking-tight">
            {description}
          </p>
        </div>

        {imageUrls && imageUrls.length > 0 ? (
          <div className="relative h-[165px] overflow-hidden rounded-md bg-gray-100">
            <img
              src={imageUrls[currentImageIndex]}
              alt={imageAlt || "Memo attachment"}
              className="h-full w-full object-cover"
            />
            {imageUrls.length > 1 && (
              <>
                <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-between px-2">
                  <button
                    type="button"
                    onClick={prevImage}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md"
                  >
                    <ChevronLeft className="h-4 w-4 text-[#354267]" />
                  </button>
                  <button
                    type="button"
                    onClick={nextImage}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-md"
                  >
                    <ChevronRight className="h-4 w-4 text-[#354267]" />
                  </button>
                </div>
                <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                  {imageUrls.map((url, index) => (
                    <div
                      key={url}
                      className={`h-1.5 rounded-full transition-all ${
                        index === currentImageIndex ? "w-5 bg-[#354267]" : "w-1.5 bg-slate-500"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          // <div className="w-full h-[165px] bg-slate-100 rounded-md" />
          <div />
        )}

        {buttonText && (
          <Button variant="outline" onClick={onButtonClick} className="w-full">
            {buttonText}
          </Button>
        )}
      </Card>
    )
  }
)
MemoListCard.displayName = "MemoListCard"

interface RoomListCardProps extends BaseCardProps {
  title: string
  badge?: React.ReactNode
  details: Array<{ subtitle: string; text: string }>
  buttonText?: string
  onButtonClick?: () => void
}

const RoomListCard = React.forwardRef<HTMLDivElement, RoomListCardProps>(
  ({ title, badge, details, buttonText, onButtonClick, className, ...props }, ref) => (
    <Card ref={ref} className={cn("p-4 space-y-2", className)} {...props}>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Home className="w-[18px] h-[18px] text-slate-400" />
            <h3 className="text-base font-semibold text-slate-600 tracking-tight leading-5">
              {title}
            </h3>
          </div>
          {badge}
        </div>

        <div className="grid grid-cols-2 gap-3 py-2.5">
          {details.map((detail) => (
            <div key={`detail-${detail.subtitle}`} className="flex flex-col gap-0.5">
              <p className="text-xs font-semibold text-slate-400 leading-[14px] tracking-tight">
                {detail.subtitle}
              </p>
              <p className="text-sm font-medium text-slate-600 leading-5 tracking-tight">
                {detail.text}
              </p>
            </div>
          ))}
        </div>
      </div>

      {buttonText && (
        <Button variant="outline" onClick={onButtonClick} className="w-full">
          {buttonText}
        </Button>
      )}
    </Card>
  )
)
RoomListCard.displayName = "RoomListCard"

interface NotificationItem {
  id: string
  title: string
  updatedAt: string
  onClick?: () => void
}

interface NotificationCardProps extends BaseCardProps {
  notifications: NotificationItem[]
}

const NotificationCard = React.forwardRef<HTMLDivElement, NotificationCardProps>(
  ({ notifications, className, ...props }, ref) => (
    <Card ref={ref} className={cn("shadow-sm", className)} {...props}>
      <div className="px-4 py-2">
        <div className="space-y-3">
          {notifications.map((notification, index) => (
            <div key={notification.id}>
              <button
                type="button"
                className="w-full flex items-center justify-between py-2 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors text-left"
                onClick={notification.onClick}
              >
                <div className="flex-1 space-y-1">
                  <p className="text-xs text-slate-400">更新日: {notification.updatedAt}</p>
                  <p className="text-sm font-semibold text-slate-600">{notification.title}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-[#354267]" />
              </button>
              {index < notifications.length - 1 && <div className="border-b border-slate-200" />}
            </div>
          ))}
        </div>
      </div>
    </Card>
  )
)
NotificationCard.displayName = "NotificationCard"

interface PendingTaskItem {
  id: string
  title: string
  count: number
  helpText?: React.ReactNode
  onClick?: () => void
}

interface PendingTaskCardProps extends BaseCardProps {
  tasks: PendingTaskItem[]
}

function PendingTaskHelpButton({ title, children }: { title: string; children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${title}の説明を表示`}
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[#354267] hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#354267]/30"
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <HelpCircle className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(300px,calc(100vw-32px))] text-xs leading-5 text-slate-600"
        align="start"
        side="bottom"
        sideOffset={8}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

const PendingTaskCard = React.forwardRef<HTMLDivElement, PendingTaskCardProps>(
  ({ tasks, className, ...props }, ref) => (
    <Card ref={ref} className={cn("shadow-sm", className)} {...props}>
      <CardContent className="p-4">
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <div key={task.id}>
              <div className="flex w-full items-center justify-between gap-2 py-2 -mx-2 px-2 rounded-md transition-colors hover:bg-slate-50">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <button
                    type="button"
                    className="min-w-0 text-left text-sm font-semibold text-slate-600"
                    onClick={task.onClick}
                  >
                    {task.title}
                  </button>
                  {task.helpText && (
                    <PendingTaskHelpButton title={task.title}>
                      {task.helpText}
                    </PendingTaskHelpButton>
                  )}
                </div>
                <button
                  type="button"
                  className="flex shrink-0 items-center gap-2"
                  onClick={task.onClick}
                >
                  <Badge
                    variant="destructive"
                    className="bg-[#FF3232] hover:bg-[#FF3232] text-white font-bold text-sm px-2 py-0.5"
                  >
                    {task.count}件
                  </Badge>
                  <ArrowRight className="h-5 w-5 text-[#354267]" />
                </button>
              </div>
              {index < tasks.length - 1 && <div className="border-b border-slate-200" />}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
)
PendingTaskCard.displayName = "PendingTaskCard"

export {
  Card,
  CardContent,
  CardHeader,
  DefaultCard,
  SummaryCard,
  ChatListCard,
  EmptyCard,
  DateCard,
  PropertyListCard,
  MemoListCard,
  RoomListCard,
  NotificationCard,
  PendingTaskCard,
}

export type { SummaryCardItem }
